import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../types/user';

interface UserContextType {
  authUser: any;
  profile: UserProfile | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  authUser: null,
  profile: null,
  loading: true,
  error: '',
  refresh: async () => {},
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [authUser, setAuthUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUser = async () => {
    setLoading(true);
    setError('');
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) {
      setAuthUser(null);
      setProfile(null);
      setError(error?.message || 'No user');
      setLoading(false);
      return;
    }
    setAuthUser(data.user);
    const { data: userData } = await supabase
      .from('users')
      .select('id, name, email, role, avatar_url, is_premium, premium_until')
      .eq('id', data.user.id)
      .single();
    if (userData) {
      setProfile(userData);
    } else {
      // Create a new user if it doesn't exist
      const { data: newUser, error: createError } = await supabase
        .from('users')
        .insert({
          id: data.user.id,
          name: '',
          email: data.user.email || '',
          role: 'User',
          avatar_url: '',
          is_premium: false,
        })
        .select()
        .single();
      
      if (createError) {
        console.error('Error creating user:', createError);
        setProfile(null);
      } else {
        setProfile(newUser);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUser();
    const { data: listener } = supabase.auth.onAuthStateChange(() => {
      fetchUser();
    });
    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider value={{ authUser, profile, loading, error, refresh: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);

// Custom hook to require authentication and redirect to /login if not authenticated
export const useRequireAuth = () => {
  const { authUser, loading } = useUser();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!loading && !authUser) {
      navigate('/login', { replace: true });
    }
  }, [authUser, loading, navigate]);
  return { authUser, loading };
}; 